import type { MapId, PermanentUpgrades, SerializedSimulation } from '../engine/types.js';
import type { PermanentUpgradeId } from '../config/balance.js';
import type { UserProfile } from '../app/profile.js';
import { loadProfile, saveProfile } from '../app/profile.js';

export interface ApiUser {
  username: string;
  fragments: number;
  upgrades: PermanentUpgrades;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  day: number;
}

interface ProfileBundle {
  user?: {
    id: number;
    username: string;
    music_user_id?: number;
  } | null;
  profile?: {
    fragments: number;
    best_score?: number;
    best_day?: number;
    tutorial_seen?: number;
  };
  upgrades?: Record<string, number>;
  maps?: Record<string, { unlocked: boolean; best_score: number; best_day: number }>;
  csrf?: string;
}

const SSO_TRIED = 'pulsenetwork.ssoTried';

async function apiFetch<T>(
  path: string,
  init?: RequestInit & { csrf?: string | null },
): Promise<{ ok: boolean; status: number; data: T | null }> {
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    };
    if (init?.csrf) headers['X-CSRF-Token'] = init.csrf;
    const { csrf: _c, ...rest } = init ?? {};
    const res = await fetch(`/api/${path}`, {
      credentials: 'include',
      headers,
      ...rest,
    });
    const data = (await res.json().catch(() => null)) as T | null;
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

export class ApiClient {
  private profile: UserProfile;
  private csrf: string | null = null;

  constructor(profile: UserProfile) {
    this.profile = profile;
  }

  getProfile(): UserProfile {
    return this.profile;
  }

  setProfile(p: UserProfile): void {
    this.profile = p;
  }

  private applyBundle(bundle: ProfileBundle): void {
    if (bundle.csrf) this.csrf = bundle.csrf;
    if (!bundle.user) return;
    const upgrades: PermanentUpgrades = { ...this.profile.upgrades };
    if (bundle.upgrades) {
      for (const [k, v] of Object.entries(bundle.upgrades)) {
        (upgrades as Record<string, number>)[k] = v;
      }
    }
    this.profile = {
      ...this.profile,
      loggedIn: true,
      username: bundle.user.username,
      fragments: bundle.profile?.fragments ?? this.profile.fragments,
      upgrades,
      tutorialDone: this.profile.tutorialDone || !!bundle.profile?.tutorial_seen,
    };
    const mapList = Array.isArray(bundle.maps)
      ? bundle.maps
      : bundle.maps
        ? Object.entries(bundle.maps).map(([map_id, m]) => ({ map_id, ...m }))
        : [];
    for (const m of mapList as Array<{ map_id: string; best_score?: number; best_day?: number }>) {
      const id = m.map_id as MapId;
      if (this.profile.records[id]) {
        this.profile.records[id] = {
          bestScore: Math.max(this.profile.records[id].bestScore, m.best_score || 0),
          bestDay: Math.max(this.profile.records[id].bestDay, m.best_day || 0),
        };
        this.profile.progressDays[id] = Math.max(
          this.profile.progressDays[id] ?? 0,
          m.best_day || 0,
        );
      }
    }
    saveProfile(this.profile);
  }

  async ensureCsrf(): Promise<string | null> {
    if (this.csrf) return this.csrf;
    const res = await apiFetch<ProfileBundle>('auth.php?action=me');
    if (res.data?.csrf) this.csrf = res.data.csrf;
    if (res.data?.user) this.applyBundle(res.data);
    return this.csrf;
  }

  /**
   * Auto-login via MusicGuess silent SSO (top-level redirect).
   * Iframes cannot read MUSICGUESSSID (host-only cookie) — redirect is required.
   */
  async trySilentSso(): Promise<void> {
    // Clean leftover query flags from SSO return
    const url = new URL(window.location.href);
    if (url.searchParams.has('sso_silent') || url.searchParams.has('sso_failed')) {
      url.searchParams.delete('sso_silent');
      url.searchParams.delete('sso_failed');
      url.searchParams.delete('next');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    const me = await apiFetch<ProfileBundle>('auth.php?action=me');
    if (me.data?.user) {
      this.applyBundle(me.data);
      return;
    }
    if (me.data?.csrf) this.csrf = me.data.csrf;

    // Local flag may be stale if the Pulse cookie expired — clear it.
    if (this.profile.loggedIn) {
      this.profile = { ...this.profile, loggedIn: false, username: null };
      saveProfile(this.profile);
    }

    if (sessionStorage.getItem(SSO_TRIED)) return;

    const start = await apiFetch<{ url?: string }>('auth.php?action=sso_start&next=/&silent=1');
    if (!start.data?.url) return;

    sessionStorage.setItem(SSO_TRIED, '1');
    // Top-level redirect so MusicGuess can read its own session cookie.
    window.location.href = start.data.url;
  }

  async login(username: string, password: string): Promise<{ ok: boolean; error?: string }> {
    const csrf = await this.ensureCsrf();
    if (!csrf) return { ok: false, error: 'Session indisponible. Recharge la page.' };
    const res = await apiFetch<ProfileBundle & { error?: string }>('auth.php', {
      method: 'POST',
      csrf,
      body: JSON.stringify({ action: 'login', username, password, csrf }),
    });
    if (res.status === 0) return { ok: false, error: 'offline' };
    if (res.data?.error) return { ok: false, error: res.data.error };
    if (!res.ok || !res.data?.user) return { ok: false, error: 'Pseudo ou mot de passe incorrect.' };
    this.applyBundle(res.data);
    return { ok: true };
  }

  async register(
    username: string,
    password: string,
    email = '',
  ): Promise<{ ok: boolean; error?: string }> {
    const csrf = await this.ensureCsrf();
    if (!csrf) return { ok: false, error: 'Session indisponible. Recharge la page.' };
    const res = await apiFetch<ProfileBundle & { error?: string }>('auth.php', {
      method: 'POST',
      csrf,
      body: JSON.stringify({
        action: 'register',
        username: username.trim(),
        password,
        email: email.trim(),
        csrf,
      }),
    });
    if (res.status === 0) return { ok: false, error: 'offline' };
    if (res.data?.error) return { ok: false, error: res.data.error };
    if (!res.ok || !res.data?.user) return { ok: false, error: 'Inscription impossible.' };
    this.applyBundle(res.data);
    return { ok: true };
  }

  async ssoMusicGuess(): Promise<void> {
    const start = await apiFetch<{ url?: string }>('auth.php?action=sso_start&next=/');
    if (start.data?.url) {
      window.location.href = start.data.url;
      return;
    }
    window.location.href =
      'https://music.goodloss.fr/api/sso.php?return=' +
      encodeURIComponent('https://pulsenetwork.goodloss.fr/api/sso_callback.php?next=/');
  }

  async purchaseUpgrade(upgradeId: PermanentUpgradeId, cost: number): Promise<boolean> {
    if (this.profile.fragments < cost) return false;
    const csrf = await this.ensureCsrf();
    const res = await apiFetch<{ ok?: boolean; error?: string }>('shop.php', {
      method: 'POST',
      csrf,
      body: JSON.stringify({ upgrade_id: upgradeId, csrf }),
    });
    if (res.ok && res.data && !res.data.error) {
      const me = await apiFetch<ProfileBundle>('auth.php?action=me');
      if (me.data?.user) this.applyBundle(me.data);
      return true;
    }
    // offline fallback
    this.profile.fragments -= cost;
    const level = (this.profile.upgrades[upgradeId] ?? 0) + 1;
    this.profile.upgrades = { ...this.profile.upgrades, [upgradeId]: level };
    saveProfile(this.profile);
    return true;
  }

  async saveRun(data: SerializedSimulation): Promise<void> {
    const csrf = await this.ensureCsrf();
    await apiFetch('run.php', {
      method: 'POST',
      csrf,
      body: JSON.stringify({ action: 'save', map_id: data.state.mapId, state: data, csrf }),
    });
  }

  async finishRun(payload: {
    mapId: MapId;
    day: number;
    score: number;
    fragments: number;
    runId?: string;
    pulsesDelivered?: number;
    cablesBuilt?: number;
  }): Promise<void> {
    const csrf = await this.ensureCsrf();
    await apiFetch('run.php', {
      method: 'POST',
      csrf,
      body: JSON.stringify({
        action: 'finish',
        run_id: payload.runId ?? crypto.randomUUID(),
        map_id: payload.mapId,
        day_reached: payload.day,
        score: payload.score,
        pulses_delivered: payload.pulsesDelivered ?? 0,
        cables_built: payload.cablesBuilt ?? 0,
        csrf,
      }),
    });
  }

  async fetchLeaderboard(
    mapId: MapId,
    mode: 'score' | 'day',
  ): Promise<LeaderboardEntry[]> {
    type RawEntry = {
      rank?: number;
      username?: string;
      score?: number;
      day?: number;
      day_reached?: number;
    };
    const data = await apiFetch<{ entries?: RawEntry[]; rows?: RawEntry[] }>(
      `leaderboard.php?map_id=${mapId}&sort=${mode}`,
    );
    const raw = data.data?.entries ?? data.data?.rows;
    if (raw?.length) {
      return raw.map((e, i) => ({
        rank: e.rank ?? i + 1,
        username: e.username ?? '—',
        score: Number(e.score ?? 0),
        // API returns day_reached; normalize to day for the UI.
        day: Number(e.day ?? e.day_reached ?? 0),
      }));
    }
    const local = loadProfile();
    const rec = local.records[mapId];
    if (rec.bestScore === 0 && rec.bestDay === 0) return [];
    return [
      {
        rank: 1,
        username: local.username ?? 'Invité',
        score: rec.bestScore,
        day: rec.bestDay,
      },
    ];
  }

  syncProfileLocal(): void {
    saveProfile(this.profile);
  }
}
